import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { OrdersData } from "../../../api/types/analytics.types";
import { chartTheme } from "../../../config/chartTheme";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import { getDateTicks } from "../../../utils/chartUtils";
import styles from "./Chart.module.scss";

interface OrdersChartProps {
  data: OrdersData[];
}

const OrdersChart = ({ data }: OrdersChartProps) => {
  const { t } = useTranslation("translation");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 768);

  const handleResize = useDebounce(() => {
    setIsMobile(window.innerWidth <= 576);
    setIsTablet(window.innerWidth <= 768);
  }, 150);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Calculate evenly spaced ticks for XAxis
  const getTicks = () => getDateTicks(data);

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        {t("charts.orders.title") || "Orders per Day"}
      </h3>
      <ResponsiveContainer
        width="100%"
        height={350}
        className={styles.chartWrapper}
      >
        <BarChart
          data={data}
          margin={{
            top: 5,
            right: isMobile ? 5 : 15,
            left: isMobile ? 0 : 5,
            bottom: isMobile ? 70 : isTablet ? 60 : 50,
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartTheme.colors.grid}
          />
          <XAxis
            dataKey="date"
            stroke={chartTheme.colors.text}
            style={{
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 10 : chartTheme.font.sizes.small,
            }}
            tickFormatter={formatDate}
            ticks={getTicks()}
            domain={[data[0]?.date || "", data[data.length - 1]?.date || ""]}
            interval={0}
            angle={isMobile ? -60 : -45}
            textAnchor="end"
            height={isMobile ? 70 : isTablet ? 60 : 50}
          />
          <YAxis
            stroke={chartTheme.colors.text}
            style={{
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 10 : chartTheme.font.sizes.small,
            }}
            width={isMobile ? 30 : 40}
          />
          <Tooltip
            contentStyle={{
              background: chartTheme.colors.background,
              border: `1px solid ${chartTheme.colors.border}`,
              borderRadius: chartTheme.borderRadius.tooltip,
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 11 : chartTheme.font.sizes.base,
              padding: isMobile ? "8px" : undefined,
            }}
            labelFormatter={(label) => formatDate(label)}
          />
          <Legend
            wrapperStyle={{
              fontFamily: chartTheme.font.family,
              fontSize: chartTheme.font.sizes.base,
              color: chartTheme.colors.text,
              paddingTop: "20px",
            }}
          />
          <Bar
            dataKey="count"
            name={t("charts.orders.count") || "Orders"}
            fill={chartTheme.colors.primary}
            radius={[8, 8, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default OrdersChart;
